import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type CustomerShipTo,
  type CustomerUsual,
  type InventoryItemSummary,
  type SalesOrder,
  type SalesOrderLineItem,
  type SalesOrderSummary,
} from '../lib/api.js';
import { useCart } from '../lib/cart-store.js';
import { useRecent } from '../lib/recent-store.js';
import { toast } from '../lib/toast-store.js';
import { mapsUrl, money0, money2, shortDate } from '../lib/format.js';
import { IconPin, IconPlus, IconSearch } from '../components/icons.js';
import { Badge, orderBadge } from '../components/badge.js';
import { QueryError } from '../components/query-error.js';

export function ClientDetailScreen() {
  const { id } = useParams({ from: '/clients/$id' });
  const spireId = Number(id);
  const navigate = useNavigate();
  const setOrderCustomer = useCart((s) => s.setSelectedCustomer);

  const customer = useQuery({
    queryKey: ['customer', spireId],
    queryFn: ({ signal }) => api.customers.getById(spireId, signal),
  });
  const customerNo = customer.data?.customerNo;
  const orders = useQuery({
    queryKey: ['orders', customerNo],
    queryFn: ({ signal }) => api.salesOrders.listByCustomer(customerNo!, signal),
    enabled: !!customerNo,
  });

  const pushClient = useRecent((s) => s.pushClient);
  useEffect(() => {
    if (customer.data) pushClient({ spireId: customer.data.spireId, name: customer.data.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.data?.spireId]);

  return (
    <>
      <div className="topbar">
        <Link to="/clients" className="back-btn">
          ‹ Clients
        </Link>
      </div>

      {customer.isPending && <div className="kd-skeleton" style={{ height: 120, margin: 14 }} />}
      {customer.isError && <QueryError message={customer.error.message} onRetry={() => void customer.refetch()} />}

      {customer.isSuccess && (
        <>
          <div className="cust-hero">
            <div className="name">{customer.data.name}</div>
            <div className="addr">
              {(() => {
                const a = customer.data.address;
                const text = [a.line1, a.city, a.provState, a.postalCode].filter(Boolean).join(', ');
                const url = mapsUrl(a.line1, a.city, a.provState, a.postalCode);
                return url ? (
                  <a className="addr-link" href={url} target="_blank" rel="noopener noreferrer">
                    <IconPin size={13} /> {text}
                  </a>
                ) : (
                  text
                );
              })()}
              {customer.data.address.phone ? ` · ${customer.data.address.phone}` : ''}
            </div>
            <div className="quick-row">
              <div className="pill">
                <div className="label">Price level</div>
                <div className="val">{customer.data.sellLevel > 1 ? `Level ${customer.data.sellLevel}` : 'Standard'}</div>
              </div>
              <div className="pill">
                <div className="label">Terms</div>
                <div className="val">{customer.data.paymentTermsCode ?? customer.data.paymentTermsDescription ?? '—'}</div>
              </div>
              <div className="pill">
                <div className="label">Balance</div>
                <div className="val">{money0(customer.data.creditBalance)}</div>
              </div>
            </div>
          </div>

          {customer.data.hold && (
            <div
              style={{
                margin: '10px 14px 0',
                background: '#fce4e1',
                color: 'var(--kd-danger)',
                borderRadius: 10,
                padding: '10px 12px',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              ⚠ This account is on A/R hold — check before placing an order.
            </div>
          )}

          <ContactCard
            name={customer.data.primaryContact?.name ?? null}
            phone={customer.data.primaryContact?.phone ?? customer.data.address.phone}
            email={customer.data.primaryContact?.email ?? customer.data.address.email}
          />

          {customer.data.shipTos.length > 0 && <ShipTosSection shipTos={customer.data.shipTos} />}

          <button
            type="button"
            className="btn btn-primary"
            style={{ margin: '12px 14px 4px', width: 'calc(100% - 28px)', display: 'flex', gap: 8, justifyContent: 'center' }}
            onClick={() => {
              setOrderCustomer({
                spireId: customer.data.spireId,
                customerNo: customer.data.customerNo,
                name: customer.data.name,
                sellLevel: customer.data.sellLevel,
              });
              toast(`Building order for ${customer.data.name}`);
              void navigate({ to: '/inventory' });
            }}
          >
            <IconPlus /> Start an order
          </button>

          {(orders.data?.length ?? 0) > 0 && <SalesSnapshot orders={orders.data!} />}

          <UsualsSection
            customerNo={customer.data.customerNo}
            customer={{
              spireId: customer.data.spireId,
              customerNo: customer.data.customerNo,
              name: customer.data.name,
              sellLevel: customer.data.sellLevel,
            }}
          />

          <div className="section-title">Order history</div>
          {orders.isPending && <div className="kd-skeleton" />}
          {orders.isError && <QueryError message={orders.error.message} onRetry={() => void orders.refetch()} />}
          {orders.isSuccess && orders.data.length === 0 && (
            <div className="kd-muted" style={{ padding: '0 18px 12px' }}>No past orders.</div>
          )}
          {orders.data?.map((o) => <OrderCard key={o.spireId} order={o} />)}

          <NotesSection customerNo={customer.data.customerNo} />
        </>
      )}
    </>
  );
}

/**
 * "Their usuals" — what this customer habitually buys (last ~10 invoices,
 * aggregated server-side). One-tap add per item or add-all; lines go in as
 * Spire-priced (last-paid price is just the display estimate).
 */
function UsualsSection({
  customerNo,
  customer,
}: {
  customerNo: string;
  customer: { spireId: number; customerNo: string; name: string; sellLevel: number };
}) {
  const add = useCart((s) => s.add);
  const setOrderCustomer = useCart((s) => s.setSelectedCustomer);
  const usuals = useQuery({
    queryKey: ['usuals', customerNo],
    queryFn: ({ signal }) => api.customers.usuals(customerNo, signal),
    staleTime: 15 * 60_000,
  });

  if (usuals.isPending) {
    return (
      <>
        <div className="section-title">Their usuals</div>
        <div className="kd-skeleton" style={{ height: 60 }} />
      </>
    );
  }
  const rows = usuals.data ?? [];
  if (usuals.isError || rows.length === 0) return null;

  const addLine = (u: CustomerUsual, quiet = false) => {
    setOrderCustomer(customer);
    add({
      partNo: u.partNo,
      whseCode: u.whse ?? '',
      description: u.description,
      unitPrice: u.lastUnitPrice,
      sellMeasure: u.sellMeasure ?? undefined,
      priceOverridden: false,
      priceSource: 'usual',
    });
    if (!quiet) toast(`Added ${u.partNo}`);
  };

  return (
    <>
      <div className="section-title">Their usuals · last 10 invoices</div>
      <div className="kd-card">
        {rows.map((u) => (
          <div
            key={`${u.partNo}|${u.whse ?? ''}`}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--kd-line)' }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {u.description ?? u.partNo}
              </div>
              <div className="kd-muted" style={{ fontSize: 11.5 }}>
                {u.partNo} · {u.invoiceCount}/10 invoices · last {u.lastDate ?? '—'} @ {money2(u.lastUnitPrice)}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ flex: 'none', padding: '6px 12px', fontSize: 13 }}
              onClick={() => addLine(u)}
            >
              + Add
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-primary"
          style={{ marginTop: 10 }}
          onClick={() => {
            for (const u of rows) addLine(u, true);
            toast(`Added ${rows.length} usual items to the cart`);
          }}
        >
          Add all {rows.length} to cart
        </button>
      </div>
    </>
  );
}

function SalesSnapshot({ orders }: { orders: SalesOrderSummary[] }) {
  const count = orders.length;
  const total = orders.reduce((s, o) => s + (Number(o.total) || Number(o.totalOrdered)), 0);
  const lastDate = orders.map((o) => o.orderDate).filter(Boolean).sort().slice(-1)[0] ?? '—';
  return (
    <div className="kd-card" style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
      <Stat label="Orders" value={String(count)} />
      <Stat label="Order value" value={money2(total)} />
      <Stat label="Last order" value={lastDate} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--kd-navy)' }}>{value}</div>
      <div className="kd-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em' }}>
        {label}
      </div>
    </div>
  );
}

const SHIPTO_CAP = 25;

const fieldStyle = {
  display: 'block',
  width: '100%',
  padding: '9px 12px',
  border: '1px solid var(--kd-line)',
  borderRadius: 10,
  fontSize: 14,
  fontFamily: 'inherit',
} as const;

function NotesSection({ customerNo }: { customerNo: string }) {
  const qc = useQueryClient();
  const notes = useQuery({
    queryKey: ['customer-notes', customerNo],
    queryFn: ({ signal }) => api.customers.notes(customerNo, signal),
  });
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [adding, setAdding] = useState(false);
  const add = useMutation({
    mutationFn: () => api.customers.addNote(customerNo, { subject: subject.trim(), body: body.trim() || undefined }),
    onSuccess: () => {
      setSubject('');
      setBody('');
      setAdding(false);
      toast('Note saved');
      void qc.invalidateQueries({ queryKey: ['customer-notes', customerNo] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Could not save note', 'error'),
  });

  return (
    <>
      <div className="section-title">Notes &amp; visits</div>
      {!adding ? (
        <button type="button" className="show-more" onClick={() => setAdding(true)}>
          + Add note
        </button>
      ) : (
        <div className="kd-card">
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject (e.g. Visit — Q3 reorder)"
            maxLength={120}
            aria-label="Note subject"
            autoFocus
            style={{ ...fieldStyle, marginBottom: 8 }}
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Details (optional)"
            rows={2}
            maxLength={4000}
            aria-label="Note details"
            style={{ ...fieldStyle, resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={add.isPending}
              onClick={() => {
                setAdding(false);
                setSubject('');
                setBody('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={add.isPending || !subject.trim()}
              onClick={() => add.mutate()}
            >
              {add.isPending ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </div>
      )}
      {notes.isPending && <div className="kd-skeleton" />}
      {notes.isError && <QueryError message={notes.error.message} onRetry={() => void notes.refetch()} />}
      {notes.isSuccess && notes.data.length === 0 && (
        <div className="kd-muted" style={{ padding: '0 18px 12px' }}>No notes yet.</div>
      )}
      {notes.data?.slice(0, 20).map((n) => (
        <div key={n.spireId} className="kd-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ fontWeight: 600, color: 'var(--kd-text)' }}>{n.subject || '(no subject)'}</div>
            <div className="kd-muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{shortDate(n.createdAt)}</div>
          </div>
          {n.body && (
            <div className="kd-muted" style={{ fontSize: 13, marginTop: 4, whiteSpace: 'pre-wrap' }}>{n.body}</div>
          )}
        </div>
      ))}
    </>
  );
}

function ShipTosSection({ shipTos }: { shipTos: CustomerShipTo[] }) {
  const [q, setQ] = useState('');
  const ql = q.trim().toLowerCase();
  const filtered = ql
    ? shipTos.filter((s) =>
        [s.name, s.shipId, s.city, s.provState].some((v) => (v ?? '').toLowerCase().includes(ql)),
      )
    : shipTos;
  const shown = filtered.slice(0, SHIPTO_CAP);

  return (
    <>
      <div className="section-title">Ship-to locations · {shipTos.length}</div>
      {shipTos.length > 8 && (
        <div className="search-bar">
          <div className="search-wrap">
            <IconSearch size={16} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ship-tos" />
          </div>
        </div>
      )}
      {ql && filtered.length === 0 && (
        <div className="kd-muted" style={{ padding: '0 18px 12px' }}>No ship-tos match “{q}”.</div>
      )}
      {shown.map((s, i) => {
        const addr = [s.line1, s.city, s.provState, s.postalCode].filter(Boolean).join(', ');
        const maps = mapsUrl(s.line1, s.city, s.provState, s.postalCode, s.country);
        const contacts = s.contacts.filter((c) => c.name || c.phone || c.email);
        return (
          <div key={s.shipId ?? i} className="kd-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
              <div style={{ fontWeight: 600, color: 'var(--kd-text)' }}>{s.name || s.shipId || 'Ship-to'}</div>
              {s.isDefault && <span className="shipto-default">Default</span>}
            </div>
            <div className="kd-muted" style={{ fontSize: 12.5, marginTop: 2 }}>{addr || '—'}</div>
            {s.shipId && (
              <div className="kd-muted" style={{ fontSize: 11, marginTop: 2 }}>Ship-to ID {s.shipId}</div>
            )}
            {maps && (
              <a className="map-link" href={maps} target="_blank" rel="noopener noreferrer">
                <IconPin size={13} /> Directions
              </a>
            )}
            {contacts.map((c, j) => (
              <div key={j} className="shipto-contact">
                <span>{c.name ?? 'Contact'}</span>
                <span style={{ display: 'flex', gap: 10 }}>
                  {c.phone && <a href={`tel:${c.phone}`}>Call</a>}
                  {c.email && <a href={`mailto:${c.email}`}>Email</a>}
                </span>
              </div>
            ))}
          </div>
        );
      })}
      {filtered.length > shown.length && (
        <div className="kd-muted" style={{ padding: '0 18px 12px' }}>
          +{filtered.length - shown.length} more — search to narrow down.
        </div>
      )}
    </>
  );
}

function ContactCard({ name, phone, email }: { name: string | null; phone: string | null; email: string | null }) {
  if (!phone && !email) return null;
  const linkStyle = { textAlign: 'center' as const, textDecoration: 'none', lineHeight: '1.6' };
  return (
    <div className="kd-card" style={{ marginTop: 10 }}>
      {name && <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: 'var(--kd-text)' }}>{name}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        {phone && (
          <a className="btn btn-secondary" href={`tel:${phone}`} style={linkStyle}>
            Call {phone}
          </a>
        )}
        {email && (
          <a className="btn btn-secondary" href={`mailto:${email}`} style={linkStyle}>
            Email
          </a>
        )}
      </div>
    </div>
  );
}

function OrderCard({ order }: { order: SalesOrderSummary }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ['order', order.spireId],
    queryFn: ({ signal }) => api.salesOrders.getById(order.spireId, signal),
    enabled: open,
  });

  // Only open orders can be edited (invoiced/cancelled are locked in Spire).
  const editable = order.status === 'open';
  const onMutated = (updated: SalesOrder) => {
    qc.setQueryData(['order', order.spireId], updated);
    void qc.invalidateQueries({ queryKey: ['orders', order.customer.customerNo] });
  };

  const addToCart = useCart((s) => s.add);
  const lines = detail.data?.items.filter((l) => l.partNo) ?? [];
  const reorderAll = () => {
    for (const l of lines) {
      addToCart({
        partNo: l.partNo,
        whseCode: l.whseCode,
        description: l.description,
        unitPrice: l.unitPrice,
        sellMeasure: l.sellMeasure ?? undefined,
        qty: Math.max(1, Math.round(Number(l.orderQty)) || 1),
      });
    }
    toast(`Added ${lines.length} item${lines.length === 1 ? '' : 's'} to cart`);
  };

  return (
    <div className={`order-card${open ? ' open' : ''}`}>
      <div
        className="order-head"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <div>
          <div className="date">{order.orderDate ?? order.orderNo}</div>
          <div className="meta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            #{order.orderNo}{' '}
            <Badge variant={orderBadge(order.status, order.type).variant}>
              {orderBadge(order.status, order.type).label}
            </Badge>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="total">{money2(Number(order.total) > 0 ? order.total : order.totalOrdered)}</div>
          <span style={{ color: 'var(--kd-text-3)' }}>{open ? '▴' : '▾'}</span>
        </div>
      </div>
      {open && (
        <div>
          {detail.isPending && <div className="line-row kd-muted">Loading lines…</div>}
          {detail.isError && <div className="line-row" style={{ color: 'var(--kd-danger)' }}>{detail.error.message}</div>}
          {detail.data &&
            (editing
              ? lines.map((l) => (
                  <EditLineRow key={l.spireId} orderId={order.spireId} line={l} onMutated={onMutated} />
                ))
              : lines.map((l) => <LineRow key={l.spireId} line={l} />))}
          {editing && detail.data && (
            <AddItemRow orderId={order.spireId} onMutated={onMutated} />
          )}
          {detail.data && !editing && lines.length > 0 && (
            <button type="button" className="show-more" onClick={reorderAll}>
              Reorder all to cart
            </button>
          )}
          {detail.data && editable && (
            <button type="button" className="show-more" onClick={() => setEditing((e) => !e)}>
              {editing ? 'Done editing' : 'Edit order'}
            </button>
          )}
          {detail.data && !editable && lines.length > 0 && (
            <div className="kd-muted" style={{ padding: '2px 18px 10px', fontSize: 11.5 }}>
              This order is {order.status} — lines can’t be edited.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EditLineRow({
  orderId,
  line,
  onMutated,
}: {
  orderId: number;
  line: SalesOrderLineItem;
  onMutated: (o: SalesOrder) => void;
}) {
  const upd = useMutation({
    mutationFn: (patch: { orderQty?: string; unitPrice?: string }) =>
      api.salesOrders.updateLine(orderId, line.spireId, patch),
    onSuccess: onMutated,
    onError: (e) => toast(e instanceof Error ? e.message : 'Update failed', 'error'),
  });
  const del = useMutation({
    mutationFn: () => api.salesOrders.removeLine(orderId, line.spireId),
    onSuccess: (o) => {
      onMutated(o);
      toast(`Removed ${line.partNo}`);
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Remove failed', 'error'),
  });
  const busy = upd.isPending || del.isPending;
  const qty = Math.max(1, Math.round(Number(line.orderQty)) || 1);

  return (
    <div className="line-row">
      <div className="body">
        <div className="name">{line.description ?? line.partNo}</div>
        <div className="sku">
          {line.partNo} @ {line.whseCode} · {money2(line.unitPrice)}
        </div>
      </div>
      <div className="mini-stepper">
        <button type="button" disabled={busy || qty <= 1} onClick={() => upd.mutate({ orderQty: String(qty - 1) })}>
          −
        </button>
        <span className="v">{qty}</span>
        <button type="button" disabled={busy} onClick={() => upd.mutate({ orderQty: String(qty + 1) })}>
          +
        </button>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          if (window.confirm(`Remove ${line.partNo} from this order?`)) del.mutate();
        }}
        style={{ background: 'none', border: 'none', color: 'var(--kd-danger)', cursor: 'pointer', fontSize: 16, opacity: busy ? 0.5 : 1 }}
        aria-label={`Remove ${line.partNo}`}
      >
        ✕
      </button>
    </div>
  );
}

function AddItemRow({ orderId, onMutated }: { orderId: number; onMutated: (o: SalesOrder) => void }) {
  const [q, setQ] = useState('');
  const [activeQ, setActiveQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setActiveQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const results = useQuery({
    queryKey: ['inv-add', activeQ],
    queryFn: ({ signal }) => api.inventory.search({ q: activeQ, limit: 8 }, signal),
    enabled: activeQ.length > 0,
  });
  const add = useMutation({
    mutationFn: (it: InventoryItemSummary) =>
      api.salesOrders.addLine(orderId, {
        partNo: it.partNo,
        whse: it.whseCode,
        orderQty: '1',
        unitPrice: it.defaultSellPrice,
        ...(it.uom ? { sellMeasure: it.uom } : {}),
      }),
    onSuccess: (o, it) => {
      onMutated(o);
      toast(`Added ${it.partNo}`);
      setQ('');
      setActiveQ('');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Add failed', 'error'),
  });

  return (
    <div style={{ padding: '8px 14px 12px' }}>
      <div className="search-wrap">
        <IconSearch size={16} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add item by part #" />
      </div>
      {results.isSuccess && activeQ && results.data.length === 0 && (
        <div className="kd-muted" style={{ fontSize: 12, marginTop: 6 }}>No items match “{activeQ}”.</div>
      )}
      {results.data && results.data.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {results.data.slice(0, 8).map((it) => (
            <button
              key={it.spireId}
              type="button"
              className="shipto-option"
              disabled={add.isPending}
              onClick={() => add.mutate(it)}
            >
              <div style={{ fontWeight: 600 }}>
                {it.partNo}{' '}
                <span className="kd-muted" style={{ fontWeight: 400 }}>{money2(it.defaultSellPrice)}</span>
              </div>
              <div className="kd-muted" style={{ fontSize: 12 }}>
                {[it.description, it.whseCode].filter(Boolean).join(' · ')}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LineRow({ line }: { line: import('../lib/api.js').SalesOrderLineItem }) {
  const add = useCart((s) => s.add);
  const [added, setAdded] = useState(false);
  return (
    <div className="line-row">
      <div className="body">
        <div className="name">{line.description ?? line.partNo}</div>
        <div className="sku">{line.partNo}</div>
      </div>
      <span className="qty">
        {line.orderQty} × {money2(line.unitPrice)}
      </span>
      <button
        type="button"
        className={`add-back-btn${added ? ' added' : ''}`}
        onClick={() => {
          add({
            partNo: line.partNo,
            whseCode: line.whseCode,
            description: line.description,
            unitPrice: line.unitPrice,
            sellMeasure: line.sellMeasure ?? undefined,
            qty: Math.max(1, Math.round(Number(line.orderQty)) || 1),
          });
          setAdded(true);
          toast(`${line.partNo} added to cart`);
          window.setTimeout(() => setAdded(false), 1200);
        }}
      >
        {added ? 'Added ✓' : '+ Add'}
      </button>
    </div>
  );
}
